import React, {Suspense, lazy, useCallback, useEffect, useMemo, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useSearchParams} from 'react-router-dom';

import {BasicPage} from 'src/components/basic-page';
import {FilterInput} from 'src/components/input';
import {SectionFilter} from 'src/components/section-filter';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {NO_RESULTS_MESSAGE} from 'src/constants';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';
import {NoResultPage} from 'src/modules/entitlements';

import {BoxLoading} from '@pb/kit-component-box-loading';
import Button from '@pb/kit-component-button';
import {Group} from '@pb/kit-component-group';
import {Icon, Icons} from '@pb/kit-component-icon';
import {Tab, TabPanel, Tabs} from '@pb/kit-component-tabs-v2';
import {pluralize} from '@pb/kit-formatters';

import {SsoAccountsFilterStrip} from './components/filter/sso-accounts-filter-strip';
import {SsoAccountsFiltersBody} from './components/filter/sso-accounts-filters-body';
import {SsoAccountsTable} from './components/sso-accounts-table';
import {SECTION_NAME} from './constants';
import {useGetSsoAccounts} from './hooks/useGetSsoAccounts';
import {ssoAccountsStateActions} from './services/sso-accounts-state.slice';
import {ssoAccountsActions} from './services/sso-accounts.slice';
import {
    SSO_ACCOUNTS_TABS,
    getSsoAccountsActiveTab,
    getSsoAccountsTabSearchParams,
} from './tabs';

import {
    ssoAccountsControls,
    ssoAccountsLoadingContainer,
    ssoAccountsTabs,
} from './sso-accounts.module.styl';

import type {SsoAccountsFilters} from './models/typedef';
import type {AccessRight} from 'src/hooks/access-rights/constants';

const SsoStatistics = lazy(() => import('./statistics/sso-statistics'));

type RawDataProps = {
    onTotalCountChange: (totalCount?: number) => void,
    stickyReferenceElement?: HTMLElement,
};

const SsoAccountsRawData = ({onTotalCountChange, stickyReferenceElement}: RawDataProps) => {
    const dispatch = useDispatch();
    const {loadingError, result, cacheKeys} = useGetSsoAccounts();
    const changeSearchQuery = useCallback((value: string) => {
        const accountQuery = value.trim();
        dispatch(ssoAccountsStateActions.setSearchCriteria(accountQuery ? {accountQuery} : {}));
    }, [dispatch]);

    const clearSearchQuery = useCallback(() => {
        dispatch(ssoAccountsStateActions.setSearchCriteria({}));
    }, [dispatch]);

    const applyFilters = useCallback((filters: SsoAccountsFilters) => {
        dispatch(ssoAccountsStateActions.setFilters(filters));
    }, [dispatch]);

    const clearFilters = useCallback(() => {
        dispatch(ssoAccountsStateActions.setFilters({}));
        return {};
    }, [dispatch]);

    const filtersCount = useMemo(() => Object.keys(cacheKeys.filters).length, [cacheKeys.filters]);
    const clearAllDisabled = useMemo(() => !Object.keys(cacheKeys.filters).length
        && !cacheKeys.searchCriteria.accountQuery, [cacheKeys.filters, cacheKeys.searchCriteria.accountQuery]);
    const totalCount = result?.totalCount || 0;

    useEffect(() => {
        onTotalCountChange(result?.totalCount);
    }, [onTotalCountChange, result?.totalCount]);

    useEffect(() => {
        return () => {
            dispatch(ssoAccountsActions.clearData());
            dispatch(ssoAccountsStateActions.clearData());
        };
    }, [dispatch]);

    return (
        <ServerResponseHandler serverError={loadingError} sectionName={SECTION_NAME}>
            <div>
                <div className={ssoAccountsControls}>
                    <Group direction="v" gap="m" as="section">
                        <Group direction="h" gap="m" as="section">
                            <FilterInput
                                id="sso-accounts-filter"
                                placeholder="Search by account name or account identifier"
                                onChange={changeSearchQuery}
                                onClear={clearSearchQuery}
                                timeOut={1000}
                                width={600}
                                value={cacheKeys.searchCriteria.accountQuery || ''}
                            />
                            <SectionFilter<SsoAccountsFilters>
                                onApply={applyFilters}
                                maxHeight={610}
                                width={465}
                                onClear={clearFilters}
                                filters={cacheKeys.filters as SsoAccountsFilters}
                                getAnchor={({anchorRef, positionRef, getAnchorProps}) => (
                                    <Button
                                        variant="Type.SECONDARY"
                                        startIcon={(
                                            <Icon type={filtersCount ? Icons.FILTER_FILLED : Icons.FILTER_EMPTY}/>
                                        )}
                                        endIcon={<Icon type={Icons.ANGLE_DOWN} ref={positionRef}/>}
                                        ref={anchorRef}
                                        {...getAnchorProps()}>
                                        <b>Filter</b>
                                    </Button>
                                )}
                                defaultFilters={{}}>
                                {(filter, onChange) => (
                                    <SsoAccountsFiltersBody
                                        filter={filter}
                                        onChange={onChange}
                                    />
                                )}
                            </SectionFilter>
                        </Group>
                        <SsoAccountsFilterStrip clearAllDisabled={clearAllDisabled}/>
                    </Group>
                </div>
                <BoxLoading loading={!result}>
                    {result?.data.length ? (
                        <div className={ssoAccountsLoadingContainer}>
                            <SsoAccountsTable
                                rows={result.data}
                                totalCount={totalCount}
                                pagination={cacheKeys.pagination}
                                stickyReferenceElement={stickyReferenceElement}
                            />
                        </div>
                    ) : (
                        result && (
                            <NoResultPage
                                title={NO_RESULTS_MESSAGE}
                                description=""
                                hideIcon={true}
                            />
                        )
                    )}
                </BoxLoading>
            </div>
        </ServerResponseHandler>
    );
};

const SSO_PAGE_HEADER_ID = 'sso-accounts-page-header';

export const SsoAccounts = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [rawDataTotalCount, setRawDataTotalCount] = useState<number>();
    const [stickyReferenceElement, setStickyReferenceElement] = useState<HTMLElement>();
    const activeTab = getSsoAccountsActiveTab(searchParams);

    useEffect(() => {
        setStickyReferenceElement(document.getElementById(SSO_PAGE_HEADER_ID) ?? undefined);
    }, []);

    const changeTab = useCallback((tab: string) => {
        setSearchParams(getSsoAccountsTabSearchParams(searchParams, tab));
    }, [searchParams, setSearchParams]);

    return (
        <ServerResponseHandler
            serverError={null}
            accessRight={ACCESS_RIGHTS.SSO_ACCOUNT_STATISTICS as AccessRight}
            sectionName={SECTION_NAME}>
            <BasicPage
                title={SECTION_NAME}
                stickyHeaderId={SSO_PAGE_HEADER_ID}
                subtitle={activeTab === SSO_ACCOUNTS_TABS.RAW_DATA && rawDataTotalCount !== undefined
                    ? pluralize(`${rawDataTotalCount} Result[s]`, rawDataTotalCount)
                    : undefined}
                buttonsBlock={null}>
                <Tabs
                    aria-label="SSO accounts views"
                    className={ssoAccountsTabs}
                    value={activeTab}
                    onChange={changeTab}
                    tabPanelId="sso-accounts-tab-panel">
                    <Tab value={SSO_ACCOUNTS_TABS.RAW_DATA} caption="Raw data"/>
                    <Tab value={SSO_ACCOUNTS_TABS.STATISTIC} caption="Statistics"/>
                </Tabs>
                <TabPanel id="sso-accounts-tab-panel">
                    {activeTab === SSO_ACCOUNTS_TABS.STATISTIC ? (
                        <Suspense fallback={<BoxLoading loading={true}><div/></BoxLoading>}>
                            <SsoStatistics/>
                        </Suspense>
                    ) : (
                        <SsoAccountsRawData
                            onTotalCountChange={setRawDataTotalCount}
                            stickyReferenceElement={stickyReferenceElement}
                        />
                    )}
                </TabPanel>
            </BasicPage>
        </ServerResponseHandler>
    );
};
